import type { ReactNode } from "react";
import { Link } from "react-router-dom";
import type { INavigationNodeDto } from "@/types/filteredContentTypes";

export interface INodeLabelProps {
  node: INavigationNodeDto;
  nodeType: "folder" | "page" | "other";
  nodePath: string;
  isActive: boolean;
  hasChildren: boolean;
  isRootLevel: boolean;
  isExpanded?: boolean;
  controlsId?: string;
  className: string;
  onToggle: () => void;
}

export function NodeLabel({
  node,
  nodeType,
  nodePath,
  isActive,
  hasChildren,
  isRootLevel,
  isExpanded,
  controlsId,
  className,
  onToggle,
}: INodeLabelProps): ReactNode {
  // Root level nodes always expand on click (no page to display)
  if (isRootLevel && hasChildren) {
    return (
      <button
        type="button"
        className={className}
        onClick={onToggle}
        aria-expanded={typeof isExpanded === "boolean" ? isExpanded : undefined}
        aria-controls={controlsId}
        data-nav-tree-item
        data-testid="node-label-button"
      >
        {node.title}
      </button>
    );
  }

  // Pages with URLs render as links
  if (nodeType === "page" && nodePath) {
    return (
      <Link
        to={nodePath}
        className={className}
        aria-current={isActive ? "page" : undefined}
        onClick={(): void => window.scrollTo(0, 0)}
        data-nav-tree-item
        data-testid="node-label-link"
      >
        {node.title}
      </Link>
    );
  }

  // Items with children render as buttons
  if (hasChildren) {
    return (
      <button
        type="button"
        className={className}
        onClick={onToggle}
        aria-expanded={typeof isExpanded === "boolean" ? isExpanded : undefined}
        aria-controls={controlsId}
        data-nav-tree-item
        data-testid="node-label-button"
      >
        {node.title}
      </button>
    );
  }

  // Everything else is a span (make focusable for keyboard nav)
  return (
    <span className={className} data-nav-tree-item data-testid="node-label-text">
      {node.title}
    </span>
  );
}
